var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));

// components/style/common/src/emotion/apply-class-label.ts
import { kebabCase } from "@gs-ux-uitoolkit-common/shared";
function applyClassLabel(styleSheetName, className, cssProperties) {
  return __spreadProps(__spreadValues({}, cssProperties), {
    label: createLabel(styleSheetName, className)
  });
}
function createLabel(styleSheetName, className) {
  const prefix = !styleSheetName ? "" : `-${kebabCase(styleSheetName)}`;
  return `${prefix}-${kebabCase(className)}`;
}

// components/style/common/src/emotion/emotion.ts
import createEmotion from "@emotion/css/create-instance";
import { getGlobalThis } from "@gs-ux-uitoolkit-common/shared";

// components/style/common/src/class-name-prefix.ts
var classNamePrefix = "gs-uitk-c";

// components/style/common/src/emotion/emotion.ts
var globalObj = getGlobalThis();
var globalKey = "__gs_uitk_emotion_instance";
function getEmotionInstance() {
  let instance = globalObj[globalKey];
  if (!instance) {
    instance = globalObj[globalKey] = createEmotionInstance();
  }
  return instance;
}
function createEmotionInstance({
  container,
  nonce: userNonce
} = {}) {
  const metaEl = typeof document !== "undefined" ? document.querySelector('meta[property="csp-nonce"]') : null;
  const nonce = userNonce || (metaEl == null ? void 0 : metaEl.getAttribute("content")) || void 0;
  const emotion = createEmotion({
    key: classNamePrefix,
    container,
    // In order to whitelist our <style> tag for a content security policy
    // (CSP) which restricts <style> tags by default, a nonce is needed for
    // the <style> tag we'll generate
    nonce,
    // Prepend styles into the <head> rather than append them. This allows
    // users' own CSS classes from .css files to override our components
    // via higher source order when they use the 'className' and 'classes' props
    prepend: true,
    // enable fast mode (i.e. use CSSStyleSheet.insertRule() API vs. adding
    // new <style> tags for each rule)
    speedy: true
  });
  return emotion;
}

// components/style/common/src/browser-supports-selector.ts
import { isBrowser } from "@gs-ux-uitoolkit-common/shared";
var browserSupportsSelectorResults = {};
function browserSupportsSelector(cssSelector) {
  if (!isBrowser) {
    throw new Error(
      "This browserSupportsSelector() function should only be called from a browser, not server-side."
    );
  }
  const memoizedValue = browserSupportsSelectorResults[cssSelector];
  if (typeof memoizedValue === "boolean") {
    return memoizedValue;
  }
  const styleEl = document.createElement("style");
  let isSupported;
  try {
    document.head.appendChild(styleEl);
    styleEl.sheet.insertRule(`${cssSelector} {}`);
    isSupported = true;
  } catch (e) {
    isSupported = false;
  } finally {
    styleEl.remove();
  }
  return browserSupportsSelectorResults[cssSelector] = isSupported;
}

// components/style/common/src/nested-class-references/interpolation-regex.ts
var interpolationRe = /#\{\s*([\w-]+)\s*\}/g;

// components/style/common/src/nested-class-references/util.ts
function isNestedPropertiesObj(value) {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

// components/style/common/src/nested-class-references/get-class-names-in-dependency-order.ts
function getClassNamesInDependencyOrder(classDefinitionsObject) {
  const classNames = Object.keys(classDefinitionsObject);
  const classNamesSet = new Set(classNames);
  const graph = new DirectedGraph(classNames);
  classNames.forEach((className) => {
    const nestedSelectors = getNestedSelectors(classDefinitionsObject[className]);
    nestedSelectors.forEach((nestedSelector) => {
      const classDependencies = parseClassDependencies(nestedSelector);
      classDependencies.forEach((depClassName) => {
        if (depClassName === className) {
          throw new Error(
            `The dependency on the class name '${depClassName}' refers to itself in the selector: '${nestedSelector}'. Use the '&' character to refer to the parent class/selector`
          );
        } else if (!classNamesSet.has(depClassName)) {
          throw new Error(
            `The dependency on the class name '${depClassName}' does not refer to another class in the StyleSheet. The full selector is: '${nestedSelector}'`
          );
        } else {
          graph.addEdge(className, depClassName);
        }
      });
    });
  });
  return topologicalSort(graph);
}
var DirectedGraph = class {
  constructor(classNames) {
    this.vertices = {};
    classNames.forEach((className) => {
      this.vertices[className] = /* @__PURE__ */ new Set();
    });
  }
  addEdge(from, to) {
    this.vertices[from].add(to);
  }
  getVertices() {
    return Object.keys(this.vertices);
  }
  // Returns the vertices adjacent to the given `vertex`
  adj(vertex) {
    return [...this.vertices[vertex]];
  }
};
function topologicalSort(graph) {
  const seen = {};
  const onStack = {};
  const edgeTo = {};
  const result = [];
  graph.getVertices().forEach((vertex) => {
    if (!seen[vertex])
      dfs(vertex);
  });
  return result;
  function dfs(vertex) {
    seen[vertex] = true;
    onStack[vertex] = true;
    graph.adj(vertex).forEach((w) => {
      if (!seen[w]) {
        edgeTo[w] = vertex;
        dfs(w);
      } else if (onStack[w]) {
        const cycle = [];
        for (let x = vertex; x !== w; x = edgeTo[x]) {
          cycle.push(x);
        }
        cycle.push(w);
        cycle.push(vertex);
        throw new Error(
          `A cycle was detected in nested selectors: #{${cycle.join(
            "} -> #{"
          )}}. The CSS class references used in nested selectors cannot reference each other circularly`
        );
      }
    });
    onStack[vertex] = false;
    result.push(vertex);
  }
}
function parseClassDependencies(selector) {
  const result = [];
  let match;
  while (match = interpolationRe.exec(selector)) {
    result.push(match[1]);
  }
  return result;
}
function getNestedSelectors(cssProperties) {
  const result = [];
  Object.keys(cssProperties).forEach((prop) => {
    const value = cssProperties[prop];
    if (isNestedPropertiesObj(value)) {
      const selector = prop;
      result.push(selector);
      result.push(...getNestedSelectors(value));
    }
  });
  return result;
}

// components/style/common/src/nested-class-references/interpolate-nested-selectors.ts
function interpolateNestedSelectors(cssProperties, mountedCssClasses) {
  const keys = Object.keys(cssProperties);
  let i = 0;
  const keysLen = keys.length;
  for (; i < keysLen; i++) {
    const key = keys[i];
    const value = cssProperties[key];
    if (isNestedPropertiesObj(value)) {
      if (interpolationRe.test(key)) {
        break;
      } else {
        cssProperties[key] = interpolateNestedSelectors(value, mountedCssClasses);
      }
    }
  }
  if (i === keysLen) {
    return cssProperties;
  }
  const result = {};
  for (let j = 0; j < i; j++) {
    const key = keys[j];
    result[key] = cssProperties[key];
  }
  for (let j = i; j < keysLen; j++) {
    const key = keys[j];
    const value = cssProperties[key];
    if (!isNestedPropertiesObj(value)) {
      result[key] = cssProperties[key];
    } else {
      const selector = key;
      const newSelector = selector.replace(interpolationRe, (_match, className) => {
        return `.${mountedCssClasses[className]}`;
      });
      result[newSelector] = interpolateNestedSelectors(value, mountedCssClasses);
    }
  }
  return result;
}

// components/style/common/src/props-memoization/mounted-style-sheet.ts
function createMountedStyleSheet(jsStyleSheetName, classDefinitions, props, emotionInstance) {
  const classDefinitionsObject = typeof classDefinitions === "function" ? classDefinitions(props) : classDefinitions;
  const resultClasses = {};
  const classNames = getClassNamesInDependencyOrder(classDefinitionsObject);
  classNames.forEach((className) => {
    const cssProperties = classDefinitionsObject[className];
    const interpolatedNestedSelectors = interpolateNestedSelectors(
      cssProperties,
      resultClasses
    );
    let finalCssProperties = interpolatedNestedSelectors;
    if (true) {
      finalCssProperties = applyClassLabel(
        jsStyleSheetName,
        className,
        interpolatedNestedSelectors
      );
    }
    resultClasses[className] = emotionInstance.css(finalCssProperties);
  });
  return new MountedStyleSheet(resultClasses);
}
var MountedStyleSheet = class {
  // the number of references (components) using this set of CSS classes
  constructor(cssClasses) {
    this.cssClasses = cssClasses;
    this.referenceCount = 1;
  }
  /**
   * Increments the reference count of keys (components) using this StyleSheet.
   */
  incrementReferenceCount() {
    this.referenceCount++;
  }
  /**
   * Decrements the reference count of the MountedStyleSheet, and if the reference
   * count has reached 0, removes the StyleSheet from the DOM.
   *
   * @return `true` if the StyleSheet has been removed from the DOM, `false` otherwise.
   */
  decrementReferenceCount() {
    this.referenceCount--;
    if (this.referenceCount === 0) {
      return true;
    } else {
      return false;
    }
  }
};

// components/style/common/src/props-memoization/props-trie.ts
var PropsTrie = class {
  constructor() {
    this.root = new PropsTrieNode();
  }
  /**
   * Retrieves the value associated with the properties of the given `props`
   * object.
   *
   *     propsTrie.set({ a: 1, b: 2 }, 'str1');
   *     propsTrie.get({ a: 1, b: 2 });  // 'str1' - note how the same object as provided to set() is not needed, the key/value combination of the props are the "links" in the trie
   */
  get(props) {
    let node = this.root;
    if (props === null) {
      return node.value;
    } else {
      for (const key of Object.keys(props)) {
        const propValue = props[key];
        node = node.get(key, propValue);
        if (!node) {
          return void 0;
        }
      }
      return node.value;
    }
  }
  /**
   * Set a value to be associated with the properties of the given `props`
   * object.
   *
   *     propsTrie.set({ a: 1, b: 2 }, 'str1');
   *     propsTrie.get({ a: 1, b: 2 });  // 'str1' - note how the same object as provided to set() is not needed, the key/value combination of the props are the "links" in the trie
   */
  set(props, value) {
    let node = this.root;
    if (props === null) {
      node.value = value;
    } else {
      for (const key of Object.keys(props)) {
        const propValue = props[key];
        let childNode = node.get(key, propValue);
        if (!childNode) {
          childNode = new PropsTrieNode();
          node.set(key, propValue, childNode);
        }
        node = childNode;
      }
      node.value = value;
    }
  }
  /**
   * Deletes a value from being associated with the properties of the given
   * `props` object.
   *
   *     propsTrie.set({ a: 1, b: 2 }, 'str1');
   *     propsTrie.get({ a: 1, b: 2 });  // 'str1'
   *
   *     propsTrie.delete({ a: 1, b: 2 });  // note how the same object as provided to set() is not needed, the key/value combination of the props are the "links" in the trie
   *     propsTrie.get({ a: 1, b: 2 });  // `undefined`
   */
  delete(props) {
    let node = this.root;
    if (props === null) {
      node.value = void 0;
    } else {
      const nodeStack = [{ key: void 0, propValue: void 0, node }];
      for (const key of Object.keys(props)) {
        const propValue = props[key];
        const childNode = node.get(key, propValue);
        if (!childNode) {
          return;
        }
        nodeStack.push({ key, propValue, node: childNode });
        node = childNode;
      }
      node.value = void 0;
      for (let i = nodeStack.length - 1; i > 0; i--) {
        const parent = nodeStack[i - 1].node;
        const current = nodeStack[i];
        const currentNode = current.node;
        if (currentNode.value === void 0 && currentNode.size === 0) {
          parent.delete(current.key, current.propValue);
        }
      }
    }
  }
  /**
   * Clears all entries from the PropsTrie
   */
  clear() {
    this.root = new PropsTrieNode();
  }
};
var PropsTrieNode = class {
  constructor() {
    this.children = /* @__PURE__ */ new Map();
    this.value = void 0;
  }
  get size() {
    return this.children.size;
  }
  /**
   * Retrieves a child PropsTrieNode given the key/propValue pair that forms
   * the edge to the node.
   */
  get(key, propValue) {
    const propertyValueMap = this.children.get(key);
    return propertyValueMap && propertyValueMap.get(propValue);
  }
  /**
   * Sets a child PropsTrieNode given the key/propValue pair that should form
   * the edge to the new node.
   */
  set(key, propValue, node) {
    let propertyValueMap = this.children.get(key);
    if (!propertyValueMap) {
      propertyValueMap = /* @__PURE__ */ new Map();
      this.children.set(key, propertyValueMap);
    }
    propertyValueMap.set(propValue, node);
  }
  /**
   * Deletes a child PropsTrieNode given the key/propValue pair that forms
   * the edge to the node.
   */
  delete(key, propValue) {
    const propertyValueMap = this.children.get(key);
    if (propertyValueMap) {
      propertyValueMap.delete(propValue);
      if (propertyValueMap.size === 0) {
        this.children.delete(key);
      }
    }
  }
};

// components/style/common/src/style-sheet.ts
import { isBrowser as isBrowser2 } from "@gs-ux-uitoolkit-common/shared";
import { shallowEqualObjects } from "shallow-equal";
var StyleSheet = class {
  constructor(name, classDefinitions) {
    this.name = name;
    this.classDefinitions = classDefinitions;
    /**
     * Holds references to the mounted CssClasses, keyed by both the `key` from
     * the {@link #mount} method and the `props` object that the StyleSheet
     * was mounted with. `key` + `props` = `MountedStyleSheet`
     *
     * This is used to unmount the correct stylesheet from the DOM when it is no
     * longer used, or to simply reduce the reference count on shared usages of
     * the StyleSheet if component instances which use the CSS classes are
     * still alive.
     */
    this.mountedStyleSheets = new PropsTrie();
    /**
     * A Map which determines if the stylesheet was mounted for the given `key`,
     * and which Props object it was mounted with for that key.
     *
     * This is used when {@link #mount} is called with the same key to determine
     * if a new `Props` object has been used for the `key`, or the same `Props`
     * object has been used for the `key`. If it is a new object, the previous
     * stylesheet is unmounted and a new one (for the new `Props` object) is
     * mounted.
     */
    this.mountedPropsObjects = /* @__PURE__ */ new Map();
  }
  /**
   * Using the {@link #classDefinitions}, creates or updates the stylesheet in the DOM
   * based on the given `props` which is passed to the {@link #classDefinitions}.
   *
   * Example:
   *
   *     const styleSheet = new StyleSheet('alert', ({ theme }: AlertStyleSheetProps) => ({
   *         root: {
   *             padding: '5px',
   *             backgroundColor: theme.bgColor,
   *         },
   *         dismissible: {
   *             paddingRight: '44px'
   *         },
   *     }));
   *
   *     // ...
   *
   *     const classes = styleSheet.mount(componentInstance, {
   *         theme: { bgColor: 'blue' }
   *     );
   *
   * `mount()` returns an object that maps the original class names to the
   * generated unique CSS class names that are mounted in the DOM. For
   * example:
   *
   *     {
   *         root: 'gs-uitk-c-1teaf32--alert-root',
   *         dismissible: 'gs-uitk-c-f794ba61--alert-dismissible',
   *     }
   *
   * ## Memoization
   *
   * The `mount()` method has a memoization layer built in where mounting the
   * same `props` object will result in a no-op as far as style generation goes.
   * Basically, if it's detected that we've already mounted the given set of
   * `props`, then the generated style rules would be exactly the same and
   * therefore we don't have to process them again.
   *
   * @param key The key to mount the stylesheet under. This is for reference counting
   *   purposes where StyleSheet can unmount its actual CSS code from the DOM when no
   *   components are using it anymore. The key is usually the component instance, or a
   *   ref in the case of a function component. A key can only ever mount exactly one
   *   `props` object, and if the `props` object changes for the `key`, the stylesheets
   *   will adjusted accordingly.
   * @param props The properties object to use to fill in dynamic values (specified with a
   *   function for the CSS class definitions - see above example)
   * @param [emotionInstance] By default, the global singleton Emotion instance is
   *   used. However for Next.js's App Router, we need an instance per request
   *   to populate the correct styles per request.
   */
  mount(key, props, emotionInstance) {
    emotionInstance = emotionInstance || getEmotionInstance();
    const {
      name: styleSheetName,
      classDefinitions,
      mountedStyleSheets,
      mountedPropsObjects
    } = this;
    const mountedPropsObject = mountedPropsObjects.get(key);
    if (mountedPropsObject && shallowEqualObjects(props, mountedPropsObject)) {
      const mountedStyleSheet = mountedStyleSheets.get(mountedPropsObject);
      return mountedStyleSheet.cssClasses;
    }
    if (mountedPropsObject) {
      this.unmount(key);
    }
    const previouslyMountedStyleSheet = mountedStyleSheets.get(props);
    if (previouslyMountedStyleSheet) {
      mountedPropsObjects.set(key, props);
      previouslyMountedStyleSheet.incrementReferenceCount();
      return previouslyMountedStyleSheet.cssClasses;
    } else {
      const mountedStyleSheet = createMountedStyleSheet(
        styleSheetName,
        classDefinitions,
        props,
        emotionInstance
      );
      if (isBrowser2) {
        mountedStyleSheets.set(props, mountedStyleSheet);
        mountedPropsObjects.set(key, props);
      }
      return mountedStyleSheet.cssClasses;
    }
  }
  /**
   * When the stylesheet is no longer needed, unmounts it from the DOM to
   * clean up memory and improve browser selector matching performance.
   */
  unmount(key) {
    const mountedPropsObject = this.mountedPropsObjects.get(key);
    if (mountedPropsObject === void 0) {
      return;
    }
    const mountedStyleSheet = this.mountedStyleSheets.get(mountedPropsObject);
    const removedFromDom = mountedStyleSheet.decrementReferenceCount();
    if (removedFromDom) {
      this.mountedStyleSheets.delete(mountedPropsObject);
    }
    this.mountedPropsObjects.delete(key);
  }
};

// components/style/common/src/create-style-sheet.ts
function createStyleSheet(name, classDefinitions) {
  return new StyleSheet(name, classDefinitions);
}

// components/style/common/src/cx.ts
var cx = getEmotionInstance().cx;

// components/style/common/src/sr-only.ts
var srOnlyStyleSheet = new StyleSheet("sr-only", () => {
  return {
    root: {
      position: "absolute",
      width: "1px",
      height: "1px",
      padding: "0",
      margin: "-1px",
      overflow: "hidden",
      clip: "rect(0, 0, 0, 0)",
      whiteSpace: "nowrap",
      border: "0"
    }
  };
});

// components/style/common/src/inject-global.ts
var injectGlobal = getEmotionInstance().injectGlobal;

// components/style/common/src/inject-font.ts
function injectFont(fontFace, { emotionInstance = getEmotionInstance() } = {}) {
  const fontFaceSrcs = normalizeFontFaceSrcs(fontFace.src);
  const fontFaceDefinitionStrings = fontFaceSrcs.map((src) => {
    return `url("${src.url}")${src.format ? ` format("${src.format}")` : ""}`;
  });
  emotionInstance.injectGlobal({
    "@font-face": {
      fontFamily: fontFace.family,
      src: `${fontFaceDefinitionStrings.join(", ")}`,
      fontWeight: fontFace.weight,
      fontStyle: fontFace.style
    }
  });
}
function normalizeFontFaceSrcs(src) {
  if (typeof src === "string") {
    return [
      {
        url: src,
        format: parseFormat(src)
      }
    ];
  } else if (Array.isArray(src)) {
    return src.map((src2) => normalizeFontFaceSrcs(src2)).reduce((arr, item) => arr.concat(item));
  } else {
    return [
      {
        url: src.url,
        format: src.format || parseFormat(src.url)
      }
    ];
  }
}
function parseFormat(url) {
  const extension = url.match(/\.(\w+)$/);
  switch (extension && extension[1]) {
    case "woff":
    case "woff2":
    case "svg":
      return extension[1];
    case "ttf":
      return "truetype";
    case "eot":
      return "embedded-opentype";
    case "otf":
    case "otc":
    case "ttc":
      return "opentype";
    default:
      return "";
  }
}

// components/style/common/src/get-shared-css-sheet.ts
var sharedCssSheet = null;
var interceptorAdded = false;
function getSharedCssSheet() {
  if (typeof window === "undefined") {
    return null;
  }
  if (!sharedCssSheet) {
    try {
      sharedCssSheet = new CSSStyleSheet();
    } catch (e) {
    }
  }
  if (!interceptorAdded && sharedCssSheet) {
    initRuleInsertionInterceptor(getEmotionInstance(), sharedCssSheet);
    interceptorAdded = true;
  }
  return sharedCssSheet;
}
function initRuleInsertionInterceptor(emotion, sharedCssSheet2) {
  const sheet = emotion.sheet;
  const existingStyleEls = sheet.tags;
  existingStyleEls.forEach((existingStyleEl) => {
    const cssRules = existingStyleEl.sheet.cssRules;
    for (let i = 0; i < cssRules.length; i++) {
      sharedCssSheet2.insertRule(cssRules[i].cssText, sharedCssSheet2.cssRules.length);
    }
  });
  const originalInsertMethod = sheet.insert;
  sheet.insert = function wrappedStyleSheetInsert(rule) {
    originalInsertMethod.call(sheet, rule);
    try {
      sharedCssSheet2.insertRule(rule, sharedCssSheet2.cssRules.length);
    } catch (e) {
    }
  };
}

// components/style/common/src/util/padding.ts
function isCssPaddingProperty(padding) {
  return typeof padding === "object" && !Array.isArray(padding);
}
function getPadding(padding, defaultPadding) {
  if (isCssPaddingProperty(padding)) {
    const top = padding.top === void 0 ? defaultPadding.top : padding.top;
    const right = padding.right === void 0 ? defaultPadding.right : padding.right;
    const bottom = padding.bottom === void 0 ? defaultPadding.bottom : padding.bottom;
    const left = padding.left === void 0 ? defaultPadding.left : padding.left;
    return `${top} ${right} ${bottom} ${left}`;
  } else {
    return padding;
  }
}
export {
  StyleSheet,
  applyClassLabel,
  browserSupportsSelector,
  classNamePrefix,
  createEmotionInstance,
  createStyleSheet,
  cx,
  getEmotionInstance,
  getPadding,
  getSharedCssSheet,
  injectFont,
  injectGlobal,
  isCssPaddingProperty,
  srOnlyStyleSheet
};
//# sourceMappingURL=index.mjs.map
